import { Button, Pill } from "@truevault/aquilo";
import React from "react";
import { useHistory } from "react-router";
import { TaskDto } from "../../../../common/service/server/dto/TaskDto";
import { useDpas } from "../../../hooks/useDataProtectionAssessment";
import { usePrimaryOrganizationId } from "../../../hooks";
import {
  DataProtectionAssessmentDto,
  DPAType,
} from "../../../../common/service/server/dto/DataProtectionAssessmentDto";

type TaskCardProps = {
  tasks: TaskDto[];
  completed?: boolean;
};

export const DPATaskCard = ({ tasks, completed = false }: TaskCardProps) => {
  return (
    <>
      <TaskCardContainer tasks={tasks} completed={completed} />
      <hr />
    </>
  );
};

const TaskCardContainer = ({
  tasks,
  children,
  completed,
}: {
  tasks: TaskDto[];
  children?: React.ReactNode;
  completed?: boolean;
}) => {
  const history = useHistory();
  const orgId = usePrimaryOrganizationId();
  const [dpas] = useDpas(orgId);

  const hasMoreThanOneDpa = tasks.length > 1;

  const description = hasMoreThanOneDpa
    ? "Based on your Data Map, your business is required to complete the following assessments. Go to Data Protection Assessments in Privacy Docs to get started."
    : "Based on your Data Map, your business is required to complete the following assessment. Go to Data Protection Assessments in Privacy Docs to get started.";

  return (
    <div className="tw-boundary">
      <div className="p-[24px] pl-0 ">
        <div>
          <h6
            className="mt-0 mb-xs font-semibold text-sm flex-grow flex-float text-neutral-800"
            style={{ width: "75%" }}
          >
            {hasMoreThanOneDpa
              ? "Complete your Data Protection Assessments (DPA)"
              : "Complete your Data Protection Assessment (DPA)"}
          </h6>
        </div>
        <div
          className="d-flex justify-content-between align-items-center text-xs text-neutral-700"
          style={{ justifyContent: "space-between" }}
        >
          <div style={{ width: "75%" }}>
            {/* Show description */}
            <div className="mb-sm">
              <p className="text-sm text-neutral-600">{description}</p>
            </div>

            {/* Show individual DPA tasks with status pills */}
            <div className="mb-xs">
              {tasks.map((task, index) => {
                const toDo = !task.completedAt && isToDo(task, dpas);
                const pendingSignatures = !task.completedAt && isPendingSignatures(task, dpas);
                const dpaCompleted = task.completedAt;
                const inProgress = !task.completedAt && isInProgress(task, dpas);
                return (
                  <>
                    <div
                      key={task.id}
                      className="d-flex align-items-center gap-2 mb-xs ml-xs mt-xs"
                    >
                      <span className="text-sm font-medium text-neutral-800">
                        {taskKeyToDpaName[task.taskKey]}
                      </span>
                      {pendingSignatures && <Pill variant="info">Pending Signatures</Pill>}
                      {dpaCompleted && <Pill variant="completed">Completed</Pill>}
                      {toDo && <Pill variant="warning">To Do</Pill>}
                      {inProgress && <Pill variant="default">In Progress</Pill>}
                    </div>
                    {index < tasks.length - 1 && (
                      <hr style={{ borderTop: "1px solid #e8e5ee", borderBottom: 0 }} />
                    )}
                  </>
                );
              })}
            </div>

            {children}
          </div>
          {!completed && (
            <div style={{ width: "200px" }}>
              <Button
                color="tertiary"
                onClick={() => history.push("/my-documents/data-protection-assessments")}
                width="full"
              >
                {"Go To Privacy Docs"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const taskKeyToDpaType: Record<string, DPAType> = {
  DPA_TARGETED_ADVERTISING_TASK: "TARGETED_ADVERTISING",
  DPA_DATA_SELLING_TASK: "DATA_SELLING",
  DPA_DATA_SELLING_GOOGLE_ANALYTICS_TASK: "DATA_SELLING_GOOGLE_ANALYTICS",
  DPA_DATA_SELLING_OTHER_TASK: "DATA_SELLING_OTHER",
  DPA_DATA_PROFILING_TASK: "PROFILING",
  DPA_CHILDREN_DATA_TASK: "PERSONAL_DATA_OF_CHILDREN",
  DPA_SENSITIVE_DATA_TASK: "SENSITIVE_DATA",
  DPA_SNI_TASK: "SNI",
};

const taskKeyToDpaName: Record<string, string> = {
  DPA_TARGETED_ADVERTISING_TASK: "Targeted Advertising",
  DPA_DATA_SELLING_TASK: "Data Selling",
  DPA_DATA_SELLING_GOOGLE_ANALYTICS_TASK: "Data Selling (Google Analytics)",
  DPA_DATA_SELLING_OTHER_TASK: "Data Selling (Other)",
  DPA_DATA_PROFILING_TASK: "Data Profiling",
  DPA_CHILDREN_DATA_TASK: "Children's Data",
  DPA_SENSITIVE_DATA_TASK: "Sensitive Data",
  DPA_SNI_TASK: "Data Selling (Shopify Network Intelligence)",
};

export const isPendingSignatures = (
  task: TaskDto,
  dpas: DataProtectionAssessmentDto[] | undefined,
) => {
  return (
    dpas?.some(
      (dpa) => dpa.type === taskKeyToDpaType[task.taskKey] && dpa.status === "REQUESTED_SIGNATURES",
    ) || false
  );
};

export const isToDo = (task: TaskDto, dpas: DataProtectionAssessmentDto[] | undefined) => {
  return !dpas?.some(
    (dpa) => dpa.type === taskKeyToDpaType[task.taskKey] && dpa.status != "ABANDONED",
  );
};

export const isInProgress = (task: TaskDto, dpas: DataProtectionAssessmentDto[] | undefined) => {
  return (
    dpas?.some((dpa) => dpa.type === taskKeyToDpaType[task.taskKey] && dpa.status === "WRITING") ||
    false
  );
};
